import { yupResolver } from "@hookform/resolvers/yup";
import { CmSheetSectionProps } from "../../SchedaCM";
import { Controller, FieldPath, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { CmSheetFooter } from "../../Components/CmSheetFooter/CmSheetFooter";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import { StyledHideOrShowContainer } from "../../SchedaCM.styled";
import {
  StyledBodyDevelopmentComments,
  StyledBusinessAreaManagerSection,
  StyledCareerAdvancementRow,
} from "./DevelopmentComments.styled";
import { FormField } from "../../../../../../components/core/FormField/FormField";
import TextArea from "../../../../../../components/core/TextArea/TextArea";
import {
  CHARACTER_MAX_LENGTH,
  CmSheetStateEnum,
  COLLABORATOR_INCREASE_LABELS,
  PermissionsEnum,
} from "../../../../../../constants/AppConstants";
import { TextBox } from "../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import {
  CAREER_ADVANCEMENT_PROPOSAL_LABEL,
  CCNL_LEVEL_ADVANCEMENT_LABEL,
  COMMENT_BAM_LABEL,
  COMMENT_PC_LABEL,
  FEEDBACK_COLLABORATOR_LABEL,
  INTERNAL_GRADE_ADVANCEMENT_DISABLED_MESSAGE,
  INTERNAL_GRADE_ADVANCEMENT_LABEL,
  NOTE_CAREER_DEV_COMMITTEE_LABEL,
  NO_SELECTION_LABEL,
  SALARY_ADVANCEMENT_LABEL,
} from "../../../../../../constants/Dictionary";
import {
  useCmSheetDevelopmentComments,
  useUpdateCmSheetDevelopmentComments,
} from "../../../../../../hooks/cmSheet/useDevelopmentComments.hook";
import {
  CollaboratorIncreasingEnum,
  UpdateCollaboratorCmSheetDevelopmentCommentsRequest,
} from "../../../../../../types/api/collaborators.type";
import { roleHasPermission } from "../../../../../../utils/roleAuth.utils";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import { currentStateIsMoreEqualThanTargetState } from "../../../../../../utils/cmSheet.utils";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import { FontWeightEnum, IconSizeEnum } from "../../../../../../types/AppTypes";
import { RadioButton } from "../../../../../../components/core/RadioButton/RadioButton";
import { Tooltip } from "../../../../../../components/core/Tooltip/Tooltip";
import { IconManager } from "../../../../../../components/core/IconManager/IconManager";

import { theme } from "../../../../../../constants/StyledComponents";
import { ReactComponent as CheckIcon } from "../../../../../../icons/check.svg";
import { ReactComponent as CloseIcon } from "../../../../../../icons/close.svg";
import { ReactComponent as EvaluationIcon } from "../../../../../../icons/evaluate.svg";

type FormData = {
  cmComment?: string;
  businessAreaManagerComment?: string;
  careerDevelopmentCommitteeNote?: string;
  feedbackToCollaborator?: string;
  collaboratorIncreasings: {
    internalGradeIncrease?: CollaboratorIncreasingEnum;
    ccnlIncrease?: CollaboratorIncreasingEnum;
    salaryIncrease?: CollaboratorIncreasingEnum;
  };
};

export const DevelopmentComments = ({
  footer,
  cmSheetInfo,
  cmSheetStateMachineInfo,
  showFooter,
  isMyProfile,
  isInterim,
  edit,
  setEdit,
}: CmSheetSectionProps) => {
  const [hasData, setHasData] = useState<boolean | null>(null);
  const { profile } = useAppSelector((state) => state.user);
  const { data, isFetching } = useCmSheetDevelopmentComments(cmSheetInfo.id);
  const { updateComments, isPending: isUpdatingComments } =
    useUpdateCmSheetDevelopmentComments(cmSheetInfo.id);

  const getCollaboratorIncreaseIcon = (
    increasing: CollaboratorIncreasingEnum | null,
  ) => {
    switch (increasing) {
      case CollaboratorIncreasingEnum.YES:
        return (
          <IconManager size={IconSizeEnum.MEDIUM} color={theme.colors.green}>
            <CheckIcon />
          </IconManager>
        );

      case CollaboratorIncreasingEnum.NO:
        return (
          <IconManager size={IconSizeEnum.MEDIUM} color={theme.colors.red_1}>
            <CloseIcon />
          </IconManager>
        );

      case CollaboratorIncreasingEnum.ANALYSIS_REQUIRED:
        return (
          <IconManager size={IconSizeEnum.MEDIUM} color={theme.colors.orange_1}>
            <EvaluationIcon />
          </IconManager>
        );

      case null:
        return (
          <BodyTyp
            variant="3"
            fontWeight={FontWeightEnum.REGULAR}
            color={theme.colors.grey_1}
          >
            {NO_SELECTION_LABEL}
          </BodyTyp>
        );
    }
  };

  const canEditBusinessAreaManagerComment = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_DEVELOPMENT_BUSINESS_COMMENT_CM_SHEET,
        ) &&
        cmSheetStateMachineInfo.currentState ===
          CmSheetStateEnum.INTERMEDIATE_EVALUATION
      ) {
        return true;
      }
    }

    return false;
  };

  const canEditCollaboratorIncreasings = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_DEVELOPMENT_INCREASINGS_CM_SHEET,
        ) &&
        cmSheetStateMachineInfo.currentState ===
          CmSheetStateEnum.INTERMEDIATE_EVALUATION
      ) {
        return true;
      }
    }

    return false;
  };

  const canEditCareerDevelopmentCommitteeNote = () => {
    if (!isMyProfile) {
      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS) &&
        data?.isNoteCareerDevelopmentCommitteeSent
      ) {
        return true;
      }
    }

    return false;
  };

  const canEditFeedbackToCollaborator = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(
          profile,
          PermissionsEnum.EDIT_DEVELOPMENT_BUSINESS_COMMENT_CM_SHEET,
        ) &&
        cmSheetStateMachineInfo.currentState ===
          CmSheetStateEnum.INTERMEDIATE_EVALUATION &&
        data?.careerDevelopmentCommitteeNote
      ) {
        return true;
      }
    }

    return false;
  };

  const canEditCMComment = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        cmSheetStateMachineInfo.currentState ===
          CmSheetStateEnum.INTERMEDIATE_EVALUATION
      ) {
        return true;
      }
    }

    return false;
  };

  const canEdit = () => {
    if (!isMyProfile) {
      if (
        canEditBusinessAreaManagerComment() ||
        canEditFeedbackToCollaborator() ||
        canEditCMComment() ||
        canEditCollaboratorIncreasings()
      ) {
        return true;
      }
    }

    return false;
  };

  const schema = yup
    .object({
      cmComment: yup.string(),
      businessAreaManagerComment: yup.string(),
      careerDevelopmentCommitteeNote: yup.string(),
      feedbackToCollaborator: yup.string(),
      collaboratorIncreasings: yup
        .object({
          internalGradeIncrease: yup
            .mixed<CollaboratorIncreasingEnum>()
            .oneOf(Object.values(CollaboratorIncreasingEnum)),
          ccnlIncrease: yup
            .mixed<CollaboratorIncreasingEnum>()
            .oneOf(Object.values(CollaboratorIncreasingEnum)),
          salaryIncrease: yup
            .mixed<CollaboratorIncreasingEnum>()
            .oneOf(Object.values(CollaboratorIncreasingEnum)),
        })
        .required(),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const collaboratorIncreasingsError =
    errors.collaboratorIncreasings?.internalGradeIncrease?.message ??
    errors.collaboratorIncreasings?.ccnlIncrease?.message ??
    errors.collaboratorIncreasings?.salaryIncrease?.message;

  const renderCommentField = ({
    canEditField,
    canViewField,
    name,
    label,
    error,
    value,
  }: {
    canEditField: boolean;
    canViewField: boolean;
    name:
      | "cmComment"
      | "businessAreaManagerComment"
      | "careerDevelopmentCommitteeNote"
      | "feedbackToCollaborator";
    label: string;
    error?: string;
    value?: string | null;
  }) => {
    if (canEditField) {
      return (
        <FormField error={error}>
          <Controller
            name={name}
            control={control}
            render={({ field }) => (
              <TextArea
                label={label}
                maxLength={CHARACTER_MAX_LENGTH}
                {...field}
              />
            )}
          />
        </FormField>
      );
    }

    if (canViewField) {
      return <TextBox label={label} text={value} />;
    }

    return null;
  };

  const renderCareerAdvancement = ({
    name,
    label,
    disabled,
    canEditField,
    value,
  }: {
    name: FieldPath<FormData>;
    label: string;
    disabled?: boolean;
    canEditField: boolean;
    value: CollaboratorIncreasingEnum | null;
  }) => {
    if (canEditField === true) {
      return (
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value: fieldValue } }) => (
            <StyledCareerAdvancementRow>
              <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                {label}
              </BodyTyp>
              <div className="salary-evaluation-form">
                {Object.values(CollaboratorIncreasingEnum).map((state) => (
                  <div className="salary-evaluation-option" key={state}>
                    <Tooltip
                      hide={!disabled}
                      message={INTERNAL_GRADE_ADVANCEMENT_DISABLED_MESSAGE}
                    >
                      <RadioButton
                        value={state === fieldValue}
                        onChange={() => onChange(state)}
                        disabled={disabled}
                      />
                    </Tooltip>
                    <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                      {COLLABORATOR_INCREASE_LABELS[state]}
                    </BodyTyp>
                  </div>
                ))}
              </div>
            </StyledCareerAdvancementRow>
          )}
        />
      );
    } else {
      const displayValue =
        disabled && value === null ? CollaboratorIncreasingEnum.NO : value;
      const tooltipMessage =
        displayValue === null ? "" : COLLABORATOR_INCREASE_LABELS[displayValue];

      return (
        <StyledCareerAdvancementRow>
          <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
            {label}
          </BodyTyp>
          <Tooltip
            autoWidth
            hide={displayValue === null}
            message={tooltipMessage}
          >
            {getCollaboratorIncreaseIcon(displayValue)}
          </Tooltip>
        </StyledCareerAdvancementRow>
      );
    }
  };

  const onSubmit = async (formData: FormData) => {
    const body: UpdateCollaboratorCmSheetDevelopmentCommentsRequest = {
      cmComment: canEditCMComment()
        ? (formData.cmComment ?? "")
        : (data?.cmComment ?? null),
      businessAreaManagerComment: canEditBusinessAreaManagerComment()
        ? (formData.businessAreaManagerComment ?? "")
        : (data?.businessAreaManagerComment ?? null),
      feedbackToCollaborator: canEditFeedbackToCollaborator()
        ? (formData.feedbackToCollaborator ?? "")
        : (data?.feedbackToCollaborator ?? null),
      careerDevelopmentCommitteeNote: canEditCareerDevelopmentCommitteeNote()
        ? (formData.careerDevelopmentCommitteeNote ?? "")
        : (data?.careerDevelopmentCommitteeNote ?? null),
      collaboratorIncreasings: {
        internalGradeIncrease:
          data?.collaboratorIncreasings?.isInternalGradeIncreaseApplicable ===
          false
            ? null
            : (formData.collaboratorIncreasings.internalGradeIncrease ?? null),
        ccnlIncrease: formData.collaboratorIncreasings.ccnlIncrease ?? null,
        salaryIncrease: formData.collaboratorIncreasings.salaryIncrease ?? null,
      },
    };
    try {
      await updateComments(body);
    } finally {
      setHasData(null);
      setEdit(false);
    }
  };

  useEffect(() => {
    if (setEdit) {
      setEdit(false);
    }
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (isFetching || !data) return;

    reset({
      cmComment: data.cmComment ?? "",
      businessAreaManagerComment: data.businessAreaManagerComment ?? "",
      careerDevelopmentCommitteeNote: data.careerDevelopmentCommitteeNote ?? "",
      feedbackToCollaborator: data.feedbackToCollaborator ?? "",
      ...(data?.collaboratorIncreasings != null && {
        collaboratorIncreasings: {
          internalGradeIncrease:
            data?.collaboratorIncreasings?.isInternalGradeIncreaseApplicable ===
            false
              ? CollaboratorIncreasingEnum.NO
              : (data?.collaboratorIncreasings?.internalGradeIncrease ??
                undefined),
          ccnlIncrease:
            data?.collaboratorIncreasings?.ccnlIncrease ?? undefined,
          salaryIncrease:
            data?.collaboratorIncreasings?.salaryIncrease ?? undefined,
        },
      }),
    });

    if (hasData === null) {
      const newHasData = !!(
        data.cmComment ||
        data.businessAreaManagerComment ||
        data.careerDevelopmentCommitteeNote ||
        data.feedbackToCollaborator ||
        data.collaboratorIncreasings
      );
      setHasData(newHasData);
    }
  }, [data, hasData, isFetching, edit, reset]);

  useEffect(() => {
    footer(
      <CmSheetFooter
        form="developmentCommentsForm"
        disabledEditButton={!isDirty || isUpdatingComments || isFetching}
        edit={edit}
        showEdit={canEdit()}
        setEdit={setEdit}
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        showFooter={showFooter}
        isInterim={isInterim}
      />,
    );
  }, [
    isDirty,
    edit,
    hasData,
    isUpdatingComments,
    isFetching,
    data,
    cmSheetInfo,
    cmSheetStateMachineInfo,
  ]);

  const showLoader = isFetching && hasData === null;
  const showPage = !showLoader;

  const canViewBusinessAreaManagerComment = () => {
    if (!isMyProfile) {
      if (canEditBusinessAreaManagerComment()) {
        return true;
      }

      if (
        currentStateIsMoreEqualThanTargetState(
          cmSheetStateMachineInfo.currentState,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const canViewCollaboratorIncreasings = () => {
    if (!isMyProfile) {
      if (canEditCollaboratorIncreasings()) {
        return true;
      }

      if (
        currentStateIsMoreEqualThanTargetState(
          cmSheetStateMachineInfo.currentState,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const canViewCareerDevelopmentCommitteeNote = () => {
    if (!isMyProfile) {
      if (canEditCareerDevelopmentCommitteeNote()) {
        return true;
      }

      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        currentStateIsMoreEqualThanTargetState(
          cmSheetStateMachineInfo.currentState,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const canViewCMComment = () => {
    if (!isMyProfile) {
      if (canEditCMComment()) {
        return true;
      }

      if (
        currentStateIsMoreEqualThanTargetState(
          cmSheetStateMachineInfo.currentState,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        )
      ) {
        return true;
      }
    }

    return false;
  };

  const canViewFeedbackToCollaborator = () => {
    if (!isMyProfile) {
      if (canEditFeedbackToCollaborator()) {
        return true;
      }
    }

    if (
      currentStateIsMoreEqualThanTargetState(
        cmSheetStateMachineInfo.currentState,
        CmSheetStateEnum.INTERMEDIATE_EVALUATION,
      )
    ) {
      return true;
    }

    return false;
  };

  return (
    <>
      {showLoader && <Loader loading={true} className="cm-sheet-loader" />}
      <StyledHideOrShowContainer $show={showPage}>
        <Loader loading={isUpdatingComments}>
          <form id="developmentCommentsForm" onSubmit={handleSubmit(onSubmit)}>
            <StyledBodyDevelopmentComments $edit={edit}>
              {renderCommentField({
                canEditField: edit && canEditCMComment(),
                canViewField: canViewCMComment(),
                name: "cmComment",
                label: COMMENT_PC_LABEL,
                error: errors.cmComment?.message,
                value: data?.cmComment,
              })}
              <StyledBusinessAreaManagerSection
                $bordered={canViewCollaboratorIncreasings()}
                $edit={edit}
              >
                {renderCommentField({
                  canEditField: edit && canEditBusinessAreaManagerComment(),
                  canViewField: canViewBusinessAreaManagerComment(),
                  name: "businessAreaManagerComment",
                  label: COMMENT_BAM_LABEL,
                  error: errors.businessAreaManagerComment?.message,
                  value: data?.businessAreaManagerComment,
                })}
                <StyledHideOrShowContainer
                  $show={canViewCollaboratorIncreasings()}
                >
                  <FormField error={collaboratorIncreasingsError}>
                    <div className="career-advancement-panel-container">
                      <BodyTyp variant="2" fontWeight={FontWeightEnum.SEMIBOLD}>
                        {CAREER_ADVANCEMENT_PROPOSAL_LABEL}
                      </BodyTyp>
                      <div className="career-advancement-panel">
                        {renderCareerAdvancement({
                          canEditField:
                            edit && canEditCollaboratorIncreasings(),
                          name: "collaboratorIncreasings.internalGradeIncrease",
                          label: INTERNAL_GRADE_ADVANCEMENT_LABEL,
                          value:
                            data?.collaboratorIncreasings
                              ?.internalGradeIncrease ?? null,
                          disabled:
                            data?.collaboratorIncreasings
                              ?.isInternalGradeIncreaseApplicable === false,
                        })}
                        {renderCareerAdvancement({
                          canEditField:
                            edit && canEditCollaboratorIncreasings(),
                          name: "collaboratorIncreasings.ccnlIncrease",
                          label: CCNL_LEVEL_ADVANCEMENT_LABEL,
                          value:
                            data?.collaboratorIncreasings?.ccnlIncrease ?? null,
                        })}
                        {renderCareerAdvancement({
                          canEditField:
                            edit && canEditCollaboratorIncreasings(),
                          name: "collaboratorIncreasings.salaryIncrease",
                          label: SALARY_ADVANCEMENT_LABEL,
                          value:
                            data?.collaboratorIncreasings?.salaryIncrease ??
                            null,
                        })}
                      </div>
                    </div>
                  </FormField>
                </StyledHideOrShowContainer>
              </StyledBusinessAreaManagerSection>
              <div className="career-development-committee-note-container">
                {renderCommentField({
                  canEditField: edit && canEditCareerDevelopmentCommitteeNote(),
                  canViewField: canViewCareerDevelopmentCommitteeNote(),
                  name: "careerDevelopmentCommitteeNote",
                  label: NOTE_CAREER_DEV_COMMITTEE_LABEL,
                  error: errors.careerDevelopmentCommitteeNote?.message,
                  value: data?.careerDevelopmentCommitteeNote,
                })}
                {renderCommentField({
                  canEditField: edit && canEditFeedbackToCollaborator(),
                  canViewField: canViewFeedbackToCollaborator(),
                  name: "feedbackToCollaborator",
                  label: FEEDBACK_COLLABORATOR_LABEL,
                  error: errors.feedbackToCollaborator?.message,
                  value: data?.feedbackToCollaborator,
                })}
              </div>
            </StyledBodyDevelopmentComments>
          </form>
        </Loader>
      </StyledHideOrShowContainer>
    </>
  );
};
